import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiSearch,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiClock,
  FiImage,
  FiMessageSquare,
  FiEdit2,
  FiSave,
  FiX,
  FiPlus,
  FiAlertCircle,
  FiAlertTriangle,
  FiFileText,
  FiClipboard,
  FiChevronRight,
  FiCalendar,
  FiCheckCircle,
  FiFilter,
  FiGrid,
  FiList,
  FiFolder
} from 'react-icons/fi';
import '../styles-clients.css';
import useIsMobile from '../utils/useIsMobile';
import PageHelp from './PageHelp';

const STAGES = [
  { key: 'intake', label: 'Intake', color: '#435654' },
  { key: 'proofs', label: 'Proofs', color: '#304849' },
  { key: 'selection', label: 'Selection', color: '#3e5c58' },
  { key: 'retouch', label: 'Retouch', color: '#3e5c58' },
  { key: 'delivery', label: 'Delivery', color: '#10b981' },
  { key: 'complete', label: 'Complete', color: '#22c55e' }
];

const STATUS_OPTIONS = ['active', 'paused', 'completed'];

function formatDate(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatRelativeDate(value) {
  if (!value) return ', ';
  const date = new Date(value);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `${Math.abs(diffDays)}d overdue`;
  } else if (diffDays === 0) {
    return 'Due today';
  } else if (diffDays === 1) {
    return 'Due tomorrow';
  } else if (diffDays <= 7) {
    return `Due in ${diffDays}d`;
  }
  return formatDate(value);
}

function getStageIndex(stage) {
  const index = STAGES.findIndex(s => s.key === stage);
  return index >= 0 ? index : 0;
}

function getStageInfo(stage) {
  return STAGES.find(s => s.key === stage) || STAGES[0];
}

function isOverdue(job) {
  if (!job.due_date || job.status !== 'active') return false;
  return new Date(job.due_date) < new Date();
}

function isDueSoon(job) {
  if (!job.due_date || job.status !== 'active') return false;
  const dueDate = new Date(job.due_date);
  const now = new Date();
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return dueDate >= now && dueDate <= weekFromNow;
}

// Stage progress indicator component
function StageProgress({ currentStage }) {
  const currentIndex = getStageIndex(currentStage);

  return (
    <div className="job-stage-progress">
      {STAGES.map((stage, index) => (
        <div
          key={stage.key}
          className={`stage-dot ${index <= currentIndex ? 'completed' : ''} ${index === currentIndex ? 'current' : ''}`}
          style={{ '--stage-color': stage.color }}
          title={stage.label}
        />
      ))}
    </div>
  );
}

// Job card component for the dashboard
function JobCard({ job, onClick }) {
  const stageInfo = getStageInfo(job.stage);
  const overdue = isOverdue(job);
  const dueSoon = isDueSoon(job);

  return (
    <div
      className={`dashboard-job-card ${overdue ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''}`}
      onClick={onClick}
    >
      <div className="job-card-header">
        <h4>{job.title}</h4>
        <span
          className="stage-badge"
          style={{ background: stageInfo.color }}
        >
          {stageInfo.label}
        </span>
      </div>

      <div className="job-card-customer">
        <FiUsers size={14} />
        <span>{job.customer_name || 'No customer'}</span>
        {job.customer_company && <span className="company">• {job.customer_company}</span>}
      </div>

      <StageProgress currentStage={job.stage} />

      <div className="job-card-meta">
        {job.due_date && (
          <span className={`due-date ${overdue ? 'overdue' : ''} ${dueSoon ? 'due-soon' : ''}`}>
            <FiCalendar size={14} />
            {formatRelativeDate(job.due_date)}
          </span>
        )}
        <div className="job-card-stats">
          {job.galleries_count > 0 && (
            <span title="Galleries"><FiImage size={14} /> {job.galleries_count}</span>
          )}
          {job.pending_selections_count > 0 && (
            <span title="Pending selections" className="pending"><FiClock size={14} /> {job.pending_selections_count}</span>
          )}
          {job.documents_count > 0 && (
            <span title="Documents"><FiFileText size={14} /> {job.documents_count}</span>
          )}
          {job.notes_count > 0 && (
            <span title="Notes"><FiClipboard size={14} /> {job.notes_count}</span>
          )}
        </div>
      </div>

      {job.description && (
        <p className="job-card-description">{job.description}</p>
      )}

      <div className="job-card-footer">
        <span className={`status-indicator status-${job.status}`}>
          {job.status === 'active' ? 'Active' : job.status === 'paused' ? 'Paused' : 'Completed'}
        </span>
        <FiChevronRight size={18} className="open-icon" />
      </div>
    </div>
  );
}

// New job form modal
function NewJobModal({ customers, onClose, onSubmit, onCreateCustomer, saving }) {
  const [draft, setDraft] = useState({
    customer_id: '',
    title: '',
    status: 'active',
    stage: 'intake',
    description: '',
    start_date: '',
    due_date: ''
  });
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isCreatingCustomer) {
      // First create the customer, then create the job
      if (!newCustomer.name.trim()) return;
      const createdCustomer = await onCreateCustomer(newCustomer);
      if (createdCustomer?.id) {
        onSubmit({ ...draft, customer_id: createdCustomer.id });
      }
    } else {
      onSubmit(draft);
    }
  };

  const toggleCustomerMode = () => {
    setIsCreatingCustomer(!isCreatingCustomer);
    if (!isCreatingCustomer) {
      setDraft(prev => ({ ...prev, customer_id: '' }));
    } else {
      setNewCustomer({ name: '', email: '', phone: '', company: '' });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Job</h3>
          <button onClick={onClose} className="modal-close"><FiX /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="customer-section">
              <div className="customer-section-header">
                <span className="section-label">Customer</span>
                <button
                  type="button"
                  className="toggle-customer-mode"
                  onClick={toggleCustomerMode}
                >
                  {isCreatingCustomer ? (
                    <><FiUsers size={14} /> Select existing</>
                  ) : (
                    <><FiPlus size={14} /> Create new</>
                  )}
                </button>
              </div>

              {isCreatingCustomer ? (
                <div className="new-customer-inline">
                  <div className="form-row">
                    <label>
                      Name *
                      <input
                        type="text"
                        value={newCustomer.name}
                        onChange={e => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Full name"
                        required
                      />
                    </label>
                    <label>
                      Company
                      <input
                        type="text"
                        value={newCustomer.company}
                        onChange={e => setNewCustomer(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="Optional"
                      />
                    </label>
                  </div>
                  <div className="form-row">
                    <label>
                      Email
                      <input
                        type="email"
                        value={newCustomer.email}
                        onChange={e => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="email@example.com"
                      />
                    </label>
                    <label>
                      Phone
                      <input
                        type="tel"
                        value={newCustomer.phone}
                        onChange={e => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <select
                  value={draft.customer_id}
                  onChange={e => setDraft(prev => ({ ...prev, customer_id: e.target.value }))}
                  required
                >
                  <option value="">Select a customer...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>
                  ))}
                </select>
              )}
            </div>

            <label>
              Job Title
              <input
                type="text"
                value={draft.title}
                onChange={e => setDraft(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Smith Wedding"
                required
              />
            </label>
            <div className="form-row">
              <label>
                Stage
                <select
                  value={draft.stage}
                  onChange={e => setDraft(prev => ({ ...prev, stage: e.target.value }))}
                >
                  {STAGES.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select
                  value={draft.status}
                  onChange={e => setDraft(prev => ({ ...prev, status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Start Date
                <input
                  type="date"
                  value={draft.start_date}
                  onChange={e => setDraft(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </label>
              <label>
                Due Date
                <input
                  type="date"
                  value={draft.due_date}
                  onChange={e => setDraft(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </label>
            </div>
            <label>
              Description
              <textarea
                value={draft.description}
                onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Scope, deliverables, or timeline details..."
                rows={3}
              />
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : isCreatingCustomer ? 'Create Customer & Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// New customer form modal
function NewCustomerModal({ onClose, onSubmit, saving }) {
  const [draft, setDraft] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(draft);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Customer</h3>
          <button onClick={onClose} className="modal-close"><FiX /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <label>
              Name
              <input
                type="text"
                value={draft.name}
                onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                required
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={draft.email}
                onChange={e => setDraft(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </label>
            <label>
              Phone
              <input
                type="tel"
                value={draft.phone}
                onChange={e => setDraft(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
              />
            </label>
            <label>
              Company
              <input
                type="text"
                value={draft.company}
                onChange={e => setDraft(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name (optional)"
              />
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Submissions() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    by_stage: {},
    by_status: {},
    overdue: 0,
    due_this_week: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showNewJob, setShowNewJob] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile(900);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [jobsRes, customersRes] = await Promise.all([
        fetch('/api/jobs', { credentials: 'include' }),
        fetch('/api/customers', { credentials: 'include' })
      ]);

      if (!jobsRes.ok) throw new Error('Failed to load jobs');
      if (!customersRes.ok) throw new Error('Failed to load customers');

      const jobsData = await jobsRes.json();
      const customersData = await customersRes.json();

      setJobs(jobsData.jobs || []);
      setStats(jobsData.stats || {});
      setCustomers(customersData || []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Status filter
      if (statusFilter !== 'all' && job.status !== statusFilter) return false;

      // Stage filter
      if (stageFilter !== 'all' && job.stage !== stageFilter) return false;

      // Search query
      if (query.trim()) {
        const searchText = `${job.title || ''} ${job.customer_name || ''} ${job.customer_company || ''} ${job.description || ''}`.toLowerCase();
        if (!searchText.includes(query.trim().toLowerCase())) return false;
      }

      return true;
    });
  }, [jobs, query, stageFilter, statusFilter]);

  async function handleCreateJob(draft) {
    if (!draft.customer_id) return;

    try {
      setSaving(true);
      const res = await fetch(`/api/customers/${draft.customer_id}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: draft.title,
          status: draft.status,
          stage: draft.stage,
          description: draft.description,
          start_date: draft.start_date || null,
          due_date: draft.due_date || null
        })
      });

      if (!res.ok) throw new Error('Failed to create job');

      const newJob = await res.json();
      setShowNewJob(false);

      // Navigate to the new job
      navigate(`/jobs/${newJob.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create job');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateCustomer(draft, keepModalOpen = false) {
    try {
      setSaving(true);
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draft)
      });

      if (!res.ok) throw new Error('Failed to create customer');

      const newCustomer = await res.json();
      setCustomers(prev => [newCustomer, ...prev]);
      if (!keepModalOpen) {
        setShowNewCustomer(false);
      }
      return newCustomer;
    } catch (err) {
      setError(err.message || 'Failed to create customer');
      return null;
    } finally {
      setSaving(false);
    }
  }

  // Wrapper for inline customer creation from job modal
  async function handleCreateCustomerInline(draft) {
    return handleCreateCustomer(draft, true);
  }

  if (loading) {
    return (
      <div className="clients-page">
        <div className="clients-loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="clients-page dashboard-mode">
      {/* Dashboard Header */}
      <header className="dashboard-hero">
        <div className="hero-copy">
          <span className="hero-eyebrow">Client Studio</span>
          <h1>Jobs Dashboard</h1>
          <p>Track project status, manage workflows, and deliver to clients.</p>
        </div>
        <div className="hero-actions">
          <button className="ghost-btn" onClick={() => setShowNewCustomer(true)}>
            <FiUsers /> New Customer
          </button>
          <button className="primary-btn" onClick={() => setShowNewJob(true)}>
            <FiPlus /> New Job
          </button>
        </div>
      </header>
      <div className="page-toolbar">
        <PageHelp
          title="How to use Clients"
          description="Manage client jobs from intake to delivery with clear stages and deadlines."
          tips={[
            'Create customers and jobs to track each project.',
            'Use stages to move work from proofs to delivery.',
            'Filter by stage or status to find active jobs.'
          ]}
          subject="Photo Harbor Help - Clients"
        />
      </div>

      {/* Stats Overview */}
      <div className="dashboard-stats">
        <div className="stat-card total">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Jobs</span>
        </div>
        <div className="stat-card overdue" onClick={() => { setStageFilter('all'); setStatusFilter('active'); }}>
          <span className="stat-value">{stats.overdue}</span>
          <span className="stat-label">Overdue</span>
          {stats.overdue > 0 && <FiAlertTriangle className="stat-icon" />}
        </div>
        <div className="stat-card due-soon">
          <span className="stat-value">{stats.due_this_week}</span>
          <span className="stat-label">Due This Week</span>
        </div>
        {STAGES.slice(0, 4).map(stage => (
          <div
            key={stage.key}
            className={`stat-card stage ${stageFilter === stage.key ? 'active' : ''}`}
            onClick={() => setStageFilter(stageFilter === stage.key ? 'all' : stage.key)}
            style={{ '--stage-color': stage.color }}
          >
            <span className="stat-value">{stats.by_stage?.[stage.key] || 0}</span>
            <span className="stat-label">{stage.label}</span>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="dashboard-filters">
        <div className="search-field">
          <FiSearch />
          <input
            type="search"
            placeholder="Search jobs..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label><FiFilter size={14} /> Stage:</label>
          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
            <option value="all">All Stages</option>
            {STAGES.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <FiGrid />
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <FiList />
          </button>
        </div>
      </div>

      {error && (
        <div className="clients-error">
          <FiAlertCircle />
          {error}
          <button onClick={() => setError(null)}><FiX /></button>
        </div>
      )}

      {/* Jobs Grid/List */}
      {filteredJobs.length === 0 ? (
        <div className="dashboard-empty">
          <FiFolder size={48} />
          <h3>No jobs found</h3>
          <p>
            {query || stageFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters or search terms.'
              : 'Create your first job to get started.'}
          </p>
          {!query && stageFilter === 'all' && statusFilter === 'all' && (
            <button className="primary-btn" onClick={() => setShowNewJob(true)}>
              <FiPlus /> Create Job
            </button>
          )}
        </div>
      ) : (
        <div className={`dashboard-jobs ${viewMode}`}>
          {filteredJobs.map(job => (
            <JobCard
              key={job.id}
              job={job}
              onClick={() => navigate(`/jobs/${job.id}`)}
            />
          ))}
        </div>
      )}

      {/* Customers Quick Access */}
      <section className="dashboard-customers-section">
        <div className="section-header">
          <h3><FiUsers /> Customers ({customers.length})</h3>
          <button className="ghost-btn small" onClick={() => setShowNewCustomer(true)}>
            <FiPlus /> Add
          </button>
        </div>
        <div className="customers-quick-list">
          {customers.slice(0, 8).map(customer => (
            <div key={customer.id} className="customer-chip">
              <span className="customer-name">{customer.name}</span>
              {customer.company && <span className="customer-company">{customer.company}</span>}
              <span className="customer-jobs">{customer.jobs_count || 0} jobs</span>
            </div>
          ))}
          {customers.length > 8 && (
            <div className="customer-chip more">
              +{customers.length - 8} more
            </div>
          )}
        </div>
      </section>

      {/* Modals */}
      {showNewJob && (
        <NewJobModal
          customers={customers}
          onClose={() => setShowNewJob(false)}
          onSubmit={handleCreateJob}
          onCreateCustomer={handleCreateCustomerInline}
          saving={saving}
        />
      )}

      {showNewCustomer && (
        <NewCustomerModal
          onClose={() => setShowNewCustomer(false)}
          onSubmit={handleCreateCustomer}
          saving={saving}
        />
      )}
    </div>
  );
}
